/**
 * Apoio da cotacao na operacao (P-COT-1): a trava por etapa que o servidor aplica,
 * repetida aqui so para desabilitar o campo antes do pedido. Quem decide e o
 * servidor (operation/domain/edit_lock.go e proposal.go).
 */

/** Etapas em que o objeto do credito (IS, modalidade, coberturas) ainda muda. */
const etapasDoObjeto = ['em_cotacao', 'aguardando_credito', 'em_subscricao'];

/** Etapas em que o que a minuta escreve (vigencia, caixas, valores) ainda muda. */
const etapasDaMinuta = [...etapasDoObjeto, 'em_juridico', 'aguardando_comercial'];

export function cotacaoObjetoAberto(stage: string): boolean {
  return etapasDoObjeto.includes(stage);
}

export function cotacaoMinutaAberta(stage: string): boolean {
  return etapasDaMinuta.includes(stage);
}

/** As permissoes finas da cotacao (05C, 4.3). */
export const permissoesDaCotacao = {
  vigencia: 'operacoes.cotacao_vigencia.update',
  segurado: 'operacoes.cotacao_segurado.update',
  configuracao: 'operacoes.cotacao_configuracao.update',
  premio: 'operacoes.cotacao_premio.update',
  parametros: 'operacoes.cotacao_parametros.update',
  // Termos contratuais da cotacao (P-COT-2): o editor "Objetos" e as clausulas.
  textos: 'operacoes.cotacao_textos.update',
  clausulas: 'operacoes.cotacao_clausulas.update',
} as const;

/** Opcoes da situacao da cotacao, na ordem do filtro do OnPoint. */
export const situacoesDaCotacao = [
  'em_analise',
  'aguardando_emissao_corretora',
  'aguardando_emissao_seguradora',
  'emitida',
  'aguardando_corretora',
  'recusada',
  'rejeitada',
  'cancelada',
  'cotacao',
  'aguardando_pagamento_boleto',
] as const;

export const tiposDaCotacao = ['apolice', 'endosso', 'renovacao'] as const;

/**
 * Tipos que a abertura comum aceita. A cotacao de endosso nasce so pelo endosso da
 * apolice (P-END-1), que liga o endosso a cotacao: aberta pela tela da operacao, ela
 * ficava presa na emissao (defeito A3 da revisao do main). O servidor recusa o tipo.
 */
export const tiposDaAbertura = ['apolice', 'renovacao'] as const;

/**
 * Tipos de endosso do OnPoint. O cadastro deles nasce com os endossos (P-END-1); ate
 * la o filtro fica preparado e desligado.
 */
export const tiposDeEndossoDoOnPoint = [
  'Alteracao da Apolice',
  'Alteracao I.S.',
  'Alteracao Vigencia',
  'Aniversario',
  'Cancelamento',
  'Neutro',
] as const;

/** Os filtros do dialogo "Cotacoes" do OnPoint (COT-016 a COT-029). */
export interface QuoteFilters {
  number: string;
  proposal_status: string;
  created_from: string;
  created_to: string;
  proposal_type: string;
  endorsement_type_id: string;
  broker_name: string;
  policyholder_name: string;
  policyholder_document: string;
  big_risk: boolean;
  monitoring: boolean;
  step_in: boolean;
}

export const filtrosVazios: QuoteFilters = {
  number: '',
  proposal_status: '',
  created_from: '',
  created_to: '',
  proposal_type: '',
  endorsement_type_id: '',
  broker_name: '',
  policyholder_name: '',
  policyholder_document: '',
  big_risk: false,
  monitoring: false,
  step_in: false,
};

/** Quantos filtros estao aplicados, para o botao dizer que ha filtro. */
export function quantosFiltros(filtros: QuoteFilters): number {
  return Object.values(filtros).filter((valor) => valor !== '' && valor !== false)
    .length;
}

/**
 * Nome da aba do editor "Objetos" do OnPoint para um bloco da cotacao (P-COT-2):
 * "Objeto: <modalidade>", "Objeto Cobertura Adicional: <cobertura>", "Condicoes
 * Gerais: <modalidade>" e "Condicoes Especiais Cobertura Adicional: <cobertura>".
 */
export function tituloDoTermo(termo: {
  kind: string;
  name: string;
  coverage_name: string | null;
  operation_coverage_id: string | null;
}): string {
  const cobertura = termo.coverage_name || termo.name;
  switch (termo.kind) {
    case 'objeto_modalidade':
      return `Objeto: ${termo.name}`;
    case 'objeto_complementar':
      return `Objeto Modalidade Complementar: ${termo.name}`;
    case 'objeto_cobertura':
      return `Objeto Cobertura Adicional: ${cobertura}`;
    case 'condicoes_gerais':
      return `Condicoes Gerais: ${termo.name}`;
    case 'condicoes_especiais':
      return termo.operation_coverage_id
        ? `Condicoes Especiais Cobertura Adicional: ${cobertura}`
        : `Condicoes Especiais: ${termo.name}`;
    default:
      return termo.name;
  }
}
